import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  sent: boolean
  retryCount: number
}

/**
 * ErrorBoundary - Catches React errors and displays them instead of blank page
 *
 * CRITICAL for Cayu: When Claude generates code with missing imports or
 * component errors, this shows the actual error instead of a white screen.
 * Includes "Send to Chat" button for Cayu integration.
 */
export class ErrorBoundary extends Component<Props, State> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, sent: false, retryCount: 0 }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })

    // Auto-retry for Safari hooks dispatcher bug: "null is not an object (evaluating 'dispatcher.useContext')"
    // Safari sometimes fails on first render due to a JIT/module evaluation timing issue.
    // Retry up to 3 times with increasing delays to let the engine stabilize.
    const isDispatcherError = error.message?.includes('dispatcher.useContext') ||
                               error.message?.includes('dispatcher.useState') ||
                               error.message?.includes('dispatcher.useEffect')
    if (isDispatcherError && this.state.retryCount < 3) {
      const delay = (this.state.retryCount + 1) * 100
      this.retryTimer = setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          error: null,
          errorInfo: null,
          retryCount: prev.retryCount + 1
        }))
      }, delay)
      return
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo)
    // Dispatch event for cayu-sdk to handle (reports to telemetry)
    window.dispatchEvent(new CustomEvent('frontend-error', {
      detail: {
        type: 'react_error',
        message: `${error.name}: ${error.message}`,
        stack: error.stack,
        componentStack: errorInfo.componentStack || undefined,
      }
    }))
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer)
  }

  handleReload = () => {
    window.location.reload()
  }

  /**
   * Send error to Cayu chat via event dispatch
   * cayu-sdk.ts handles sending to the parent Cayu platform
   */
  handleSendToChat = () => {
    window.dispatchEvent(new CustomEvent('cayu:send-error-to-chat', {
      detail: { message: this.formatErrorForClaude() }
    }))
    this.setState({ sent: true })
    setTimeout(() => this.setState({ sent: false }), 2000)
  }

  /**
   * Format error for Claude - concise and actionable
   */
  formatErrorForClaude = (): string => {
    const { error, errorInfo } = this.state

    // Extract file and line from stack trace
    const stackLines = error?.stack?.split('\n') || []
    const relevantLine = stackLines.find(line =>
      line.includes('.tsx') || line.includes('.ts') || line.includes('.jsx') || line.includes('.js')
    ) || ''

    // Parse file location (e.g., "at Dashboard (Dashboard.tsx:15:20)")
    const fileMatch = relevantLine.match(/\(([^)]+)\)/) || relevantLine.match(/at\s+(.+)/)
    const fileLocation = fileMatch ? fileMatch[1].trim() : 'Unknown location'

    // Parse component stack to get component names
    const componentStack = errorInfo?.componentStack || ''
    const components = componentStack
      .split('\n')
      .filter(line => line.trim().startsWith('at '))
      .map(line => line.trim().replace(/^at\s+/, '').split(' ')[0])
      .filter(Boolean)
      .slice(0, 5) // First 5 components
      .join(' → ')

    // Build Claude-friendly message
    let message = `## Frontend Error\n\n`
    message += `**Error:** ${error?.name || 'Error'}: ${error?.message || 'Unknown error'}\n\n`
    message += `**Location:** ${fileLocation}\n\n`

    if (components) {
      message += `**Component Stack:** ${components}\n\n`
    }

    // Add first few relevant stack lines
    const relevantStack = stackLines
      .slice(0, 6)
      .filter(line => line.trim())
      .join('\n')

    if (relevantStack) {
      message += `**Stack Trace:**\n\`\`\`\n${relevantStack}\n\`\`\``
    }

    return message
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const { error, errorInfo, sent } = this.state
      const isDev = import.meta.env.DEV

      return (
        <div
          className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4"
          data-error-boundary="true"
          data-error-message={error?.message || 'An unexpected error occurred'}
        >
          <div className="max-w-4xl w-full">
            {/* Error Header with Actions */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-t-lg p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-xl font-semibold text-red-800 dark:text-red-200">
                      Error Detected
                    </h1>
                    <p className="mt-1 text-sm text-red-700 dark:text-red-300 break-words">
                      {error?.message || 'An unexpected error occurred'}
                    </p>
                  </div>
                </div>
                {/* Action Buttons - Right side */}
                <div className="flex flex-shrink-0 gap-2">
                  {/* Primary: Send to Chat (for Cayu) */}
                  <button
                    onClick={this.handleSendToChat}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                      sent
                        ? 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500'
                        : 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    }`}
                  >
                    {sent ? (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Sent!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Send to Chat
                      </>
                    )}
                  </button>
                  <button
                    onClick={this.handleReload}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reload
                  </button>
                </div>
              </div>
            </div>

            {/* Error Details (Dev Mode Only) */}
            {isDev && (
              <div className="bg-white dark:bg-gray-800 border-x border-b border-red-200 dark:border-red-800 rounded-b-lg p-6">
                {error?.name && (
                  <div className="mb-4">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Error Type</h2>
                    <code className="text-sm font-mono text-red-600 dark:text-red-400">{error.name}</code>
                  </div>
                )}

                {error?.stack && (
                  <div className="mb-4">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Stack Trace</h2>
                    <pre className="bg-gray-100 dark:bg-gray-900 rounded-md p-4 overflow-x-auto text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                      {error.stack}
                    </pre>
                  </div>
                )}

                {errorInfo?.componentStack && (
                  <div>
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Component Stack</h2>
                    <pre className="bg-gray-100 dark:bg-gray-900 rounded-md p-4 overflow-x-auto text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Production Mode - simple footer */}
            {!isDev && (
              <div className="bg-white dark:bg-gray-800 border-x border-b border-red-200 dark:border-red-800 rounded-b-lg p-6">
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  An error occurred. Please try again or contact support if the problem persists.
                </p>
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
